import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTranslation } from "@/i18n/translations";
import { useUserTimezone } from "@/hooks/useUserTimezone";
import { formatDate } from "@/lib/dateUtils";
import { Lock, AlertTriangle, Clock, Building2, Tag } from "lucide-react";
import RedactedValue from "@/components/project/RedactedValue";
import { DealType, Project, ProjectPhase, ProjectStatus, RiskLevel, Sector, SectorLabels } from "@/types/project";

interface OverviewTabProps {
  project: Project;
}

const OverviewTab = ({ project }: OverviewTabProps) => {
  const { language } = useLanguage();
  const tz = useUserTimezone();
  const { t } = useTranslation(language);

  const getStatusLabel = (status: ProjectStatus | null) => {
    switch (status) {
      case ProjectStatus.Active: return t("active");
      case ProjectStatus.OnHold: return t("onHold");
      case ProjectStatus.Cancelled: return t("cancelled");
      case ProjectStatus.Done: return t("done");
      default: return "-";
    }
  };

  const getRiskLabel = (risk: RiskLevel | null) => {
    switch (risk) {
      case RiskLevel.Low: return t("low");
      case RiskLevel.Medium: return t("medium");
      case RiskLevel.High: return t("high");
      default: return "-";
    }
  };

  const getDealTypeLabel = (dealType: DealType) => {
    switch (dealType) {
      case DealType.Acquisition: return t("dealTypeAcquisition");
      case DealType.Merger: return t("dealTypeMerger");
      case DealType.GrowthInvestment: return t("dealTypeGrowthInvestment");
      case DealType.PEBuyout: return t("dealTypePEBuyout");
      case DealType.CarveOut: return t("dealTypeCarveOut");
      case DealType.JointVenture: return t("dealTypeJointVenture");
      case DealType.AssetPurchase: return t("dealTypeAssetPurchase");
      default: return String(dealType);
    }
  };

  const getPhaseLabel = (phase: ProjectPhase | null) => {
    switch (phase) {
      case ProjectPhase.InitialReview: return t("initialReview");
      case ProjectPhase.DueDiligence: return t("dueDiligence");
      case ProjectPhase.Negotiation: return t("negotiation");
      case ProjectPhase.Closing: return t("closing");
      default: return "-";
    }
  };

  const StatusLamp = ({ status }: { status: ProjectStatus | null }) => {
    const color = status === ProjectStatus.Active
      ? "bg-green-500"
      : status === ProjectStatus.OnHold
        ? "bg-yellow-500"
        : status === ProjectStatus.Cancelled
          ? "bg-red-500"
          : status === ProjectStatus.Done
            ? "bg-blue-500"
            : "bg-gray-400";

    return (
      <div className="flex items-center gap-2">
        <div className={`w-3 h-3 rounded-full ${color}`} />
        <span>{getStatusLabel(status)}</span>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* KPIs Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <AlertTriangle size={14} />
              {t("risk")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
              project.risk === RiskLevel.Low ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200" :
              project.risk === RiskLevel.Medium ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200" :
              project.risk === RiskLevel.High ? "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200" :
              "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200"
            }`}>
              {getRiskLabel(project.risk)}
            </span>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Clock size={14} />
              {t("expectedCloseDate")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">
              {project.expectedClose ? formatDate(project.expectedClose, tz, language) : "-"}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Building2 size={14} />
              {t("organization")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Link to={`/organization/${project.organizationId}`} className="text-lg font-semibold text-primary hover:underline">
              {project.organizationName || "-"}
            </Link>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Lock size={14} />
              {t("status")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <StatusLamp status={project.status} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Lock size={14} />
              {t("phase")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-lg font-semibold">{getPhaseLabel(project.phase)}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Lock size={14} />
              {t("redacted")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <RedactedValue value={project.redacted} className="text-sm" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Lock size={14} />
              {t("owner")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-lg font-semibold">{project.owner || "-"}</p>
          </CardContent>
        </Card>

        {project.sector != null && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Building2 size={14} />
                {t("projectSector")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-lg font-semibold">{SectorLabels[project.sector] ?? String(project.sector)}</p>
            </CardContent>
          </Card>
        )}

        {project.dealType != null && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Tag size={14} />
                {t("projectDealType")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-lg font-semibold">{getDealTypeLabel(project.dealType)}</p>
            </CardContent>
          </Card>
        )}
      </div>

    </div>
  );
};

export default OverviewTab;
